import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  makeStyles,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
  Divider
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { useHistory, useLocation } from 'react-router-dom';
import useIsMounted from '../../../hooks/useIsMounted';
import isEqual from 'lodash/isEqual';

// Import all step components
import CMODetails, { AdminDetails } from './Stepper/steps/CMODetails';
import SiteDetails from './Stepper/steps/SiteDetails';
import PlantDetails from './Stepper/steps/PlantDetails';
import ShipmentConfigurations from './Stepper/steps/Configurations';
import PurchasingDetails from './Stepper/steps/PurchasingDetails';
import OptionsPermissions from './Stepper/steps/OptionsPermissions';
import CMOReview from './Stepper/steps/CMOReview';

// import '../CMOConfigDetails.css';

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: 0,
  },
  header: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 600,
    fontSize: 38,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 27,
    lineHeight: "100%",
  },
  sectionContainer: {
    position: "relative",
    marginBottom: 30,
    padding: "20px 32px",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 20,
    textTransform: "uppercase",
    color: "#333",
    borderBottom: "1px solid #e0e0e0",
    paddingBottom: 10,
  },
  editButton: {
    position: "absolute",
    top: 20,
    right: 32,
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: "50%",
    padding: 0,
    backgroundColor: "transparent",
    color: "#333",
    border: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  saveButton: {
    backgroundColor: "#ec6535",
    color: "white",
    marginRight: 10,
    "&:hover": {
      backgroundColor: "#d45328",
    },
  },
  saveButtonDisabled: {
    backgroundColor: "#f5f5f5",
    color: "#999",
    marginRight: 10,
    "&:hover": {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed"
    },
  },
  cancelButton: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    border: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  actionButtons: {
    position: "absolute",
    top: 20,
    right: 32,
    display: "flex",
  },
  bottomActionButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: 30,
    marginBottom: 30,
  },
  updateChangesButton: {
    backgroundColor: "#ec6535",
    color: "white",
    padding: "10px 20px",
    "&:hover": {
      backgroundColor: "#d45328",
    },
  },
  updateChangesButtonDisabled: {
    backgroundColor: "#f5f5f5",
    color: "#999",
    padding: "10px 20px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed"
    },
  },
  discardChangesButton: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    marginRight: 10,
    border: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  divider: {
    margin: "30px 0",
  }
}));

const sections = [
  { id: 'cmoDetails', title: 'CMO Details', component: CMODetails },
  { id: 'adminDetails', title: 'Admin Details', component: AdminDetails },
  { id: 'siteDetails', title: 'Site Details', component: SiteDetails },
  { id: 'plantDetails', title: 'Plant Details', component: PlantDetails },
  { id: 'plantConfigurations', title: 'Plant Configurations', component: null }, // This would need a proper component
  { id: 'shipmentConfigurations', title: 'Batch Configurations', component: ShipmentConfigurations },
  { id: 'documentTypes', title: 'Document Types', component: null }, // This would need a proper component
  { id: 'purchasingDetails', title: 'Purchasing Details', component: PurchasingDetails },
];

// Deep comparison utility function for objects and arrays
const deepCompare = (obj1, obj2) => {
  // If either is null/undefined, compare directly
  if (obj1 === null || obj2 === null || obj1 === undefined || obj2 === undefined) {
    return obj1 === obj2;
  }

  // Handle primitive types
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;

    // Sort arrays if they contain simple values to ensure consistent comparison
    if (obj1.every(item => typeof item !== 'object' || item === null)) {
      const sorted1 = [...obj1].sort();
      const sorted2 = [...obj2].sort();
      return sorted1.every((val, idx) => deepCompare(val, sorted2[idx]));
    }

    // For arrays with objects, we need to match items by their key properties if possible
    // For simplicity in this implementation, we'll just compare each element
    return obj1.every((val, idx) => deepCompare(val, obj2[idx]));
  }

  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => {
    // Skip functions in comparison
    if (typeof obj1[key] === 'function' || typeof obj2[key] === 'function') return true;
    return deepCompare(obj1[key], obj2[key]);
  });
};

const UpdateCMOConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const isMounted = useIsMounted();

  // Get data from location state
  const { rowData, cmoOrgDetails, cmoVendorDetails } = location.state || {};

  //console.log(cmoOrgDetails, "Update cmoOrgDetails")
  //console.log(cmoVendorDetails, "Update cmoVendorDetails")

  const [editMode, setEditMode] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    cmoDetails: {},
    vendorPlantDetails: {},
    adminDetails: {}
  });

  // Store initial form data for comparison
  const initialFormDataRef = useRef(null);

  // Store previous formData for comparison in the useEffect
  const prevFormDataRef = useRef(null);

  // Track changes for each section
  const [sectionChanges, setSectionChanges] = useState({});

  // Track updated data for API payload
  const [updatedData, setUpdatedData] = useState({});
  //console.log(updatedData, "updatedDatadudee")

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Track if any section has changes for the Update Changes button
  const [hasAnyChanges, setHasAnyChanges] = useState(false);

  // Initialize form data from location state
  useEffect(() => {
    if (cmoOrgDetails || cmoVendorDetails) {
      const cmoDetails = cmoOrgDetails || {};

      // Create the cmoDetails object with the exact field names expected by the component
      const cmoDetailsData = {
        cmoFullName: cmoDetails.cmoName || '',
        cmoShortName: cmoDetails.cmoShortFormName || '',
        streetAddress1: cmoDetails.streetAddress1 || '',
        streetAddress2: cmoDetails.streetAddress2 || '',
        city: cmoDetails.city || '',
        state: cmoDetails.state || '',
        postCode: cmoDetails.postCode || '',
        country: cmoDetails.country || '',
        organizationCode: cmoDetails.organizationCode || '',
        status: cmoDetails.status || 'Active',
        ecosystemId: cmoDetails.organizationId?.toString() || '',

        // Include admin details in the cmoDetails object as well
        adminFullName: cmoDetails.fullName || '',
        adminEmail: cmoDetails.emailAddress || '',
        adminPhoneNumber: cmoDetails.phoneNumber?.replace(/^\+/, '') || '', // Remove + prefix if present
        adminPhoneCode: cmoDetails.phoneNumber?.startsWith('+') ?
          cmoDetails.phoneNumber.substring(0, 3) : '+1', // Extract country code or default to +1

        // Add organizationDetails structure as well since component checks for it
        organizationDetails: {
          organizationName: cmoDetails.cmoName || '',
          orgShortName: cmoDetails.cmoShortFormName || '',
          streetAddress1: cmoDetails.streetAddress1 || '',
          streetAddress2: cmoDetails.streetAddress2 || '',
          city: cmoDetails.city || '',
          state: cmoDetails.state || '',
          postCode: cmoDetails.postCode || '',
          country: cmoDetails.country || '',
          fullName: cmoDetails.fullName || '',
          emailAddress: cmoDetails.emailAddress || '',
          phoneNumber: cmoDetails.phoneNumber || '',
        }
      };

      // Create admin details object
      const adminDetailsData = {
        fullName: cmoDetails.fullName || '',
        email: cmoDetails.emailAddress || '',
        phone: cmoDetails.phoneNumber || ''
      };

      // Get vendor plant details directly from the API response
      const vendorPlantDetails = cmoVendorDetails?.vendorPlantDetails || {};

      // Create the initial data object
      const initialData = {
        cmoDetails: cmoDetailsData,
        adminDetails: adminDetailsData,
        vendorPlantDetails: vendorPlantDetails
      };

      //console.log('Setting initial form data:', initialData);
      setFormData(initialData);

      // Store initial data for comparison
      initialFormDataRef.current = JSON.parse(JSON.stringify(initialData));
    }
  }, [cmoOrgDetails, cmoVendorDetails]);

  // Check for any changes across all sections
  useEffect(() => {
    const anyChanges = Object.values(sectionChanges).some(hasChange => hasChange);
    setHasAnyChanges(anyChanges);
  }, [sectionChanges]);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Toggle edit mode for a specific section
  const toggleEditMode = (sectionId) => {
    setEditMode(prev => {
      const newEditMode = {
        ...prev,
        [sectionId]: !prev[sectionId]
      };

      return newEditMode;
    });
  };

  // Improved check for changes function
  const checkForChanges = useCallback((sectionId, currentData) => {
    if (!initialFormDataRef.current) return false;

    const initialSectionData = initialFormDataRef.current[sectionId];
    if (!initialSectionData) return false;

    // Enhanced comparison logic
    let hasChanged = false;

    // Debug logs to help diagnose the issue
    // console.log(`Checking for changes in ${sectionId}:`, {
    //   initial: initialSectionData,
    //   current: currentData
    // });

    // For vendorPlantDetails, we need to handle nested objects and arrays
    if (sectionId === 'vendorPlantDetails') {
      // Check for new keys in current data that weren't in initial data
      const currentKeys = Object.keys(currentData);
      const initialKeys = Object.keys(initialSectionData);

      // If the number of keys is different, something has changed
      if (currentKeys.length !== initialKeys.length) {
        //console.log(`Different number of keys in ${sectionId}: initial=${initialKeys.length}, current=${currentKeys.length}`);
        hasChanged = true;
      }

      // Check each key for differences
      currentKeys.forEach(key => {
        const currentValue = currentData[key];
        const initialValue = initialSectionData[key];

        // Skip comparing functions
        if (typeof currentValue === 'function') return;

        // Special handling for arrays and objects
        if (Array.isArray(currentValue) || (typeof currentValue === 'object' && currentValue !== null)) {
          if (!deepCompare(currentValue, initialValue)) {
            // console.log(`Change detected in ${sectionId}.${key}:`, {
            //   original: initialValue,
            //   current: currentValue
            // });
            hasChanged = true;
          }
        }
        // Simple comparison for primitive values
        else if (currentValue !== initialValue) {
          //console.log(`Change detected in ${sectionId}.${key}: '${initialValue}' -> '${currentValue}'`);
          hasChanged = true;
        }
      });
    } else {
      // First check for simple differences in keys
      const currentKeys = Object.keys(currentData);
      const initialKeys = Object.keys(initialSectionData);

      if (currentKeys.length !== initialKeys.length) {
        hasChanged = true;
      } else {
        // Check each key for differences
        for (const key of currentKeys) {
          const currentValue = currentData[key];
          const initialValue = initialSectionData[key];

          // Skip comparing functions or complex objects like DOM elements
          if (typeof currentValue === 'function') continue;

          // Special handling for nested objects
          if (typeof currentValue === 'object' && currentValue !== null &&
            typeof initialValue === 'object' && initialValue !== null) {
            // Use deep comparison for objects
            if (!deepCompare(currentValue, initialValue)) {
              hasChanged = true;
              break;
            }
          }
          // Simple comparison for primitive values
          else if (currentValue !== initialValue) {
            // Convert to string for more reliable comparison
            const currentStr = String(currentValue);
            const initialStr = String(initialValue);

            if (currentStr !== initialStr) {
              hasChanged = true;
              break;
            }
          }
        }
      }
    }

    //console.log(`Section ${sectionId} has changes: ${hasChanged}`);

    // Update section changes state
    setSectionChanges(prev => {
      // Only update if the value is changing to avoid unnecessary rerenders
      if (prev[sectionId] !== hasChanged) {
        //console.log(`Updating sectionChanges for ${sectionId}: ${prev[sectionId]} -> ${hasChanged}`);
        return {
          ...prev,
          [sectionId]: hasChanged
        };
      }
      return prev;
    });

    // If changed, update the updatedData object
    if (hasChanged) {
      setUpdatedData(prev => ({
        ...prev,
        [sectionId]: currentData
      }));
    }

    return hasChanged;
  }, []);

  // Update step data
  const updateStepData = useCallback((sectionId, data) => {
    try {
      if (isMounted()) {
        //console.log(`Updating ${sectionId} with:`, data);

        // Special handling for vendorPlantDetails to ensure we're not losing data
        if (sectionId === 'vendorPlantDetails') {
          setFormData(prev => {
            // Create a deep merged object that preserves all existing data
            const updatedSection = {
              ...prev[sectionId],
              ...data
            };

            const updatedFormData = {
              ...prev,
              [sectionId]: updatedSection
            };

            // Force check for changes
            setTimeout(() => {
              checkForChanges(sectionId, updatedSection);
            }, 10);

            return updatedFormData;
          });
        } else {
          // Standard update for other sections
          setFormData(prev => {
            const updatedFormData = {
              ...prev,
              [sectionId]: data
            };

            // Force check for changes
            setTimeout(() => {
              checkForChanges(sectionId, data);
            }, 10);

            return updatedFormData;
          });
        }
      }
    } catch (error) {
      console.error(`Error updating step data for ${sectionId}:`, error);
    }
  }, [isMounted, checkForChanges]);

  // Handle save section
  const handleSaveSection = async (sectionId) => {
    try {
      setIsLoading(true);

      // Determine the actual data section ID
      const dataSectionId =
        (sectionId === 'siteDetails' || sectionId === 'plantDetails' ||
          sectionId === 'shipmentConfigurations' || sectionId === 'purchasingDetails')
          ? 'vendorPlantDetails'
          : sectionId;

      // Simulate API call with a delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get the updated data for this section
      const sectionData = formData[dataSectionId];

      // Log what we're saving
      //console.log(`Saving section ${sectionId} (data in ${dataSectionId}):`, sectionData);

      // Check if there are actual changes
      const hasChanges = checkForChanges(dataSectionId, sectionData);

      if (hasChanges) {
        // In a real app, you would make an API call here to save the changes
        //console.log(`Changes detected in ${dataSectionId}:`, updatedData[dataSectionId]);

        // Update the initialFormDataRef to reflect the saved changes
        initialFormDataRef.current = {
          ...initialFormDataRef.current,
          [dataSectionId]: JSON.parse(JSON.stringify(sectionData))
        };

        // Reset section changes state since we've saved
        setSectionChanges(prev => ({
          ...prev,
          [dataSectionId]: false
        }));

        // Clear this section from updatedData
        setUpdatedData(prev => {
          const newUpdatedData = { ...prev };
          delete newUpdatedData[dataSectionId];
          return newUpdatedData;
        });

        // Show success message
        setSnackbar({
          open: true,
          message: `${sections.find(s => s.id === sectionId)?.title || sectionId} saved successfully`,
          severity: "success"
        });
      } else {
        // No changes detected
        setSnackbar({
          open: true,
          message: "No changes detected",
          severity: "info"
        });
      }

      // Exit edit mode
      setEditMode(prev => ({
        ...prev,
        [sectionId]: false
      }));
    } catch (error) {
      console.error(`Error saving ${sectionId}:`, error);
      setSnackbar({
        open: true,
        message: `Error saving ${sections.find(s => s.id === sectionId)?.title || sectionId}: ${error.message || 'Unknown error'}`,
        severity: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing for a specific section
  const handleCancelEdit = (sectionId) => {
    // Determine the actual data section ID
    const dataSectionId =
      (sectionId === 'siteDetails' || sectionId === 'plantDetails' ||
        sectionId === 'shipmentConfigurations' || sectionId === 'purchasingDetails')
        ? 'vendorPlantDetails'
        : sectionId;

    // Revert changes by resetting to initial data
    if (initialFormDataRef.current && initialFormDataRef.current[dataSectionId]) {
      setFormData(prev => ({
        ...prev,
        [dataSectionId]: JSON.parse(JSON.stringify(initialFormDataRef.current[dataSectionId]))
      }));

      // Reset section changes
      setSectionChanges(prev => ({
        ...prev,
        [dataSectionId]: false
      }));
    }

    toggleEditMode(sectionId);
    setSnackbar({
      open: true,
      message: "Changes discarded",
      severity: "info",
    });
  };

  // Handle update all changes
  const handleUpdateAllChanges = () => {
    if (!hasAnyChanges) return;

    setIsLoading(true);

    // Here you would make an API call to update all changes
    // Combine all the data from different sections for the API payload
    const updatedSections = Object.keys(sectionChanges).filter(key => sectionChanges[key]);
    const payload = {};

    updatedSections.forEach(sectionId => {
      payload[sectionId] = formData[sectionId];
    });

    setTimeout(() => {
      // Update initial data reference after successful save
      if (initialFormDataRef.current) {
        updatedSections.forEach(sectionId => {
          initialFormDataRef.current[sectionId] = JSON.parse(JSON.stringify(formData[sectionId]));
        });
      }

      // Reset all section changes
      const resetChanges = {};
      Object.keys(sectionChanges).forEach(key => {
        resetChanges[key] = false;
      });
      setSectionChanges(resetChanges);

      setSnackbar({
        open: true,
        message: "All changes updated successfully",
        severity: "success",
      });
      setIsLoading(false);

      // Navigate back after successful update
      setTimeout(() => {
        history.goBack();
      }, 1500);
    }, 1500);
  };

  // Force check for changes when entering edit mode
  useEffect(() => {
    Object.entries(editMode).forEach(([sectionId, isEditing]) => {
      if (isEditing && formData[sectionId] && initialFormDataRef.current?.[sectionId]) {
        // Only perform a single check when entering edit mode
        checkForChanges(sectionId, formData[sectionId]);
      }
    });
  }, [editMode, formData, checkForChanges]);

  // Add a new effect to listen for changes in the child component's state
  useEffect(() => {
    // Skip this effect if we're not in edit mode for any section
    const hasEditingSection = Object.values(editMode).some(isEditing => isEditing);
    if (!hasEditingSection) return;

    // Only run if formData actually changed
    const currentFormDataStr = JSON.stringify(formData);
    if (prevFormDataRef.current === currentFormDataStr) return;

    prevFormDataRef.current = currentFormDataStr;

    // Create a debounced function to check for changes
    const timeoutId = setTimeout(() => {
      // This effect will run whenever formData changes
      // It will check for changes in all sections that are in edit mode
      Object.entries(editMode).forEach(([sectionId, isEditing]) => {
        if (isEditing && formData[sectionId]) {
          checkForChanges(sectionId, formData[sectionId]);
        }
      });
    }, 500);

    // Clean up the timeout when the component unmounts or when dependencies change
    return () => {
      clearTimeout(timeoutId);
    };
  }, [formData, editMode, checkForChanges]);

  // Render a section with its component and edit functionality
  const renderSection = (section) => {
    if (!section.component) {
      // Skip sections without components for now
      return null;
    }

    const isEditing = editMode[section.id] || false;
    const SectionComponent = section.component;

    // Get the actual section ID that contains the data
    const dataSectionId =
      (section.id === 'siteDetails' || section.id === 'plantDetails' ||
        section.id === 'shipmentConfigurations' || section.id === 'purchasingDetails')
        ? 'vendorPlantDetails'
        : section.id;

    // Check for changes in the appropriate section
    const hasChanges = sectionChanges[dataSectionId] || false;

    //console.log(`Section ${section.id} (data in ${dataSectionId}) has changes: ${hasChanges}`);

    // Prepare section-specific props based on section type
    let sectionProps = {};

    switch (section.id) {
      case 'cmoDetails':
        sectionProps = {
          formData: formData.cmoDetails || {},
          updateStepData: (data) => {
            updateStepData('cmoDetails', data);
            // Force check for changes
            setTimeout(() => {
              checkForChanges('cmoDetails', { ...formData.cmoDetails, ...data });
            }, 10);
          },
          hideAdminDetails: true
        };
        break;

      case 'adminDetails':
        sectionProps = {
          formData: {
            adminFullName: formData.cmoDetails?.adminFullName || '',
            adminEmail: formData.cmoDetails?.adminEmail || '',
            adminPhoneNumber: formData.cmoDetails?.adminPhoneNumber || '',
            adminPhoneCode: formData.cmoDetails?.adminPhoneCode || ''
          },
          updateStepData: (data) => {
            // Update both adminDetails and the admin fields in cmoDetails
            updateStepData('adminDetails', {
              fullName: data.adminFullName,
              email: data.adminEmail,
              phone: data.adminPhoneNumber
            });
            updateStepData('cmoDetails', data);

            // Force check for changes
            setTimeout(() => {
              checkForChanges('adminDetails', {
                fullName: data.adminFullName,
                email: data.adminEmail,
                phone: data.adminPhoneNumber
              });
              checkForChanges('cmoDetails', { ...formData.cmoDetails, ...data });
            }, 10);
          }
        };
        break;

      case 'siteDetails':
        // Extract site data from the API response
        const siteData = cmoVendorDetails?.vendorPlantDetails || {};

        sectionProps = {
          formData: {
            siteID: formData.vendorPlantDetails?.siteID || siteData?.siteID || '',
            siteIDShortName: formData.vendorPlantDetails?.siteIDShortName || siteData?.siteIDShortName || '',
            siteAddressLine1: formData.vendorPlantDetails?.siteAddressLine1 || siteData?.siteAddressLine1 || '',
            siteAddressLine2: formData.vendorPlantDetails?.siteAddressLine2 || siteData?.siteAddressLine2 || '',
            country: formData.vendorPlantDetails?.country || siteData?.country || '',
            state: formData.vendorPlantDetails?.state || siteData?.state || '',
            city: formData.vendorPlantDetails?.city || siteData?.city || '',
            zipCode: formData.vendorPlantDetails?.zipCode || siteData?.zipCode || ''
          },
          updateStepData: (data) => {
            // Create a merged update with all existing vendorPlantDetails
            const updatedData = {
              ...formData.vendorPlantDetails,
              ...data
            };

            // Update vendorPlantDetails with the merged data
            updateStepData('vendorPlantDetails', updatedData);

            // Force check for changes immediately
            setTimeout(() => {
              checkForChanges('vendorPlantDetails', updatedData);
            }, 10);
          }
        };
        break;

      case 'plantDetails':
        // Extract plant data from the API response
        const plantData = cmoVendorDetails?.vendorPlantDetails || {};

        sectionProps = {
          formData: {
            // Pass the new cmoSites structure
            cmoSites: formData.vendorPlantDetails?.cmoSites || plantData?.cmoSites || [],
           
            plantCode: formData.vendorPlantDetails?.plantCode || plantData?.plantCode || '',
            plantCodeShortName: formData.vendorPlantDetails?.plantCodeShortName || plantData?.plantCodeShortName || '',
            mainAddress: formData.vendorPlantDetails?.mainAddress || plantData?.mainAddress || {},
            pickupAddress: formData.vendorPlantDetails?.pickupAddress || plantData?.pickupAddress || {},
            deliveryAddress: formData.vendorPlantDetails?.deliveryAddress || plantData?.deliveryAddress || {},
            // Convert arrays of objects to arrays of IDs for the component
            drugTypeIDList: formData.vendorPlantDetails?.drugTypeIDs?.map(item => Number(item.drugTypeID)) ||
              formData.vendorPlantDetails?.drugTypeIDList?.map(id => Number(id)) ||
              plantData?.drugTypeIDs?.map(item => Number(item.drugTypeID)) ||
              plantData?.drugTypeIDList?.map(id => Number(id)) || [],
            supplyTypeIDList: formData.vendorPlantDetails?.supplyTypeIDs?.map(item => Number(item.supplyTypeID)) ||
              formData.vendorPlantDetails?.supplyTypeIDList?.map(id => Number(id)) ||
              plantData?.supplyTypeIDs?.map(item => Number(item.supplyTypeID)) ||
              plantData?.supplyTypeIDList?.map(id => Number(id)) || [],
            plantTypeIDList: formData.vendorPlantDetails?.plantTypeIDs?.map(item => Number(item.plantTypeID)) ||
              formData.vendorPlantDetails?.plantTypeIDList?.map(id => Number(id)) ||
              plantData?.plantTypeIDs?.map(item => Number(item.plantTypeID)) ||
              plantData?.plantTypeIDList?.map(id => Number(id)) || [],
            facilityTypeIDList: formData.vendorPlantDetails?.facilityTypeIDs?.map(item => Number(item.facilityTypeID)) ||
              formData.vendorPlantDetails?.facilityTypeIDList?.map(id => Number(id)) ||
              plantData?.facilityTypeIDs?.map(item => Number(item.facilityTypeID)) ||
              plantData?.facilityTypeIDList?.map(id => Number(id)) || []
          },
          selectedCMOFormData: cmoVendorDetails,
          originalVendorPlantDetails: cmoVendorDetails,
          updateStepData: (data) => {
            // Create a merged update with all existing vendorPlantDetails
            const updatedData = {
              ...formData.vendorPlantDetails,
              ...data
            };

            // Update vendorPlantDetails with the merged data
            updateStepData('vendorPlantDetails', updatedData);

            // Force check for changes immediately
            setTimeout(() => {
              checkForChanges('vendorPlantDetails', updatedData);
            }, 10);
          },
          readOnly: !isEditing,
          hideStepActions: true
        };
        break;

      case 'shipmentConfigurations':
        // Extract shipment data from the API response
        const shipmentData = cmoVendorDetails?.vendorPlantDetails || {};

        sectionProps = {
          formData: {
            shipmentDuration: formData.vendorPlantDetails?.shipmentDuration || shipmentData?.shipmentDuration || '',
            cmoDocumentTypesList: formData.vendorPlantDetails?.cmoDocumentTypesList || [],
          },
          updateStepData: (data) => {
            // Create a merged update with all existing vendorPlantDetails
            const updatedData = {
              ...formData.vendorPlantDetails,
              ...data
            };

            // Update vendorPlantDetails with the merged data
            updateStepData('vendorPlantDetails', updatedData);

            // Force check for changes immediately
            setTimeout(() => {
              checkForChanges('vendorPlantDetails', updatedData);
            }, 10);
          }
        };
        break;

      case 'purchasingDetails':
        // Extract purchasing data from the API response
        const purchasingData = cmoVendorDetails?.vendorPlantDetails || {};

        sectionProps = {
          formData: {
            companyCode: formData.vendorPlantDetails?.companyCode || purchasingData?.companyCode || '',
            // Transform poDocumentTypesList to poDocType array
            poDocType: formData.vendorPlantDetails?.poDocumentTypesList?.map(type => type.poDocumentType) ||
              purchasingData?.poDocumentTypesList?.map(type => type.poDocumentType) || [],
            // Transform purchasingOrgList to array of objects with code and description
            purchasingOrgList: formData.vendorPlantDetails?.purchasingOrgList?.map(org => ({
              code: org.purchasingOrgCode,
              description: org.purchasingOrgDescription
            })) || purchasingData?.purchasingOrgList?.map(org => ({
              code: org.purchasingOrgCode,
              description: org.purchasingOrgDescription
            })) || [],
            // Transform purchasingGroupList to array of objects with code, description and telephoneNbr
            purchasingGroupList: formData.vendorPlantDetails?.purchasingGroupList?.map(group => ({
              code: group.purchasingGroupCode,
              description: group.purchasingGroupDescription,
              telephoneNbr: group.purchasingGroupTelephone
            })) || purchasingData?.purchasingGroupList?.map(group => ({
              code: group.purchasingGroupCode,
              description: group.purchasingGroupDescription,
              telephoneNbr: group.purchasingGroupTelephone
            })) || [],
            cmoIDFromSponsorERP: formData.vendorPlantDetails?.cmoIDFromSponsorERP || purchasingData?.cmoIDFromSponsorERP || ''
          },
          updateStepData: (data) => {
            // Create a merged update with all existing vendorPlantDetails
            const updatedData = {
              ...formData.vendorPlantDetails,
              ...data
            };

            // Update vendorPlantDetails with the merged data
            updateStepData('vendorPlantDetails', updatedData);

            // Force check for changes immediately
            setTimeout(() => {
              checkForChanges('vendorPlantDetails', updatedData);
            }, 10);
          }
        };
        break;

      default:
        sectionProps = {};
    }

    // Common props for all components
    const commonProps = {
      readOnly: !isEditing,
      hideStepActions: true,
      reportChangesImmediately: true,
      onFieldChange: (fieldName, fieldValue) => {
        //console.log(`Parent received field change: ${fieldName} = ${fieldValue} for section ${section.id}`);

        // Create an update object with just this field
        const updateObj = { [fieldName]: fieldValue };

        // Update the formData based on section
        if (section.id === 'cmoDetails') {
          updateStepData('cmoDetails', updateObj);
        } else if (section.id === 'adminDetails') {
          updateStepData('adminDetails', {
            fullName: fieldName === 'adminFullName' ? fieldValue : formData.adminDetails.fullName,
            email: fieldName === 'adminEmail' ? fieldValue : formData.adminDetails.email,
            phone: fieldName === 'adminPhoneNumber' ? fieldValue : formData.adminDetails.phone
          });
          updateStepData('cmoDetails', updateObj);
        } else if (section.id === 'siteDetails' || section.id === 'plantDetails' ||
          section.id === 'shipmentConfigurations' || section.id === 'purchasingDetails') {
          updateStepData('vendorPlantDetails', updateObj);
        }

        // Force check for changes
        setTimeout(() => {
          if (section.id === 'cmoDetails') {
            checkForChanges('cmoDetails', { ...formData.cmoDetails, ...updateObj });
          } else if (section.id === 'adminDetails') {
            checkForChanges('adminDetails', {
              fullName: fieldName === 'adminFullName' ? fieldValue : formData.adminDetails.fullName,
              email: fieldName === 'adminEmail' ? fieldValue : formData.adminDetails.email,
              phone: fieldName === 'adminPhoneNumber' ? fieldValue : formData.adminDetails.phone
            });
            checkForChanges('cmoDetails', { ...formData.cmoDetails, ...updateObj });
          } else if (section.id === 'siteDetails' || section.id === 'plantDetails' ||
            section.id === 'shipmentConfigurations' || section.id === 'purchasingDetails') {
            checkForChanges('vendorPlantDetails', { ...formData.vendorPlantDetails, ...updateObj });
          }
        }, 10);
      }
    };

    return (
      <Box key={section.id} className={classes.sectionContainer}>
        <Typography className={classes.sectionTitle}>{section.title}</Typography>

        {isEditing ? (
          <Box className={classes.actionButtons}>
            <Button
              variant="contained"
              className={hasChanges ? classes.saveButton : classes.saveButtonDisabled}
              startIcon={<SaveIcon />}
              onClick={() => handleSaveSection(section.id)}
              disabled={isLoading || !hasChanges}
            >
              Save Changes
              {isLoading && <CircularProgress size={24} style={{ marginLeft: 10, color: 'white' }} />}
            </Button>
            <Button
              variant="contained"
              className={classes.cancelButton}
              startIcon={<CancelIcon />}
              onClick={() => handleCancelEdit(section.id)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            className={classes.editButton}
            onClick={() => toggleEditMode(section.id)}
          >
            <EditIcon />
          </Button>
        )}

        <SectionComponent {...commonProps} {...sectionProps} />

        <Divider className={classes.divider} />
      </Box>
    );
  };

  // Render the main Update Changes button
  const renderUpdateChangesButton = () => {
    // Log the current state of changes
    //console.log('Section changes:', sectionChanges);
    //console.log('Has any changes:', hasAnyChanges);

    return (
      <Box className={classes.bottomActionButtons}>
        <Button
          variant="contained"
          className={hasAnyChanges ? classes.updateChangesButton : classes.updateChangesButtonDisabled}
          disabled={!hasAnyChanges || isLoading}
          onClick={handleUpdateAllChanges}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Update Changes"}
        </Button>
      </Box>
    );
  };

  return (
    <div className="update-cmo-config-wrapper">
      <Paper className={classes.root}>
        <Box className={classes.header}>
          <Typography className={classes.heading} variant="h5">
            Update CMO Configuration
          </Typography>
          <Typography className={classes.subheading}>
            Update the CMO details and configurations in the fields below.
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            Cancel
            <CloseIcon />
          </Box>
        </Box>

        <Box p={3}>
          {sections.map((section) => renderSection(section))}

          {renderUpdateChangesButton()}
        </Box>

        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Paper>
    </div>
  );
};

export default UpdateCMOConfig;